import React, {useEffect, useState} from "react";
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next';

import api from "../utils/api";
import {
	allowedFoodLabel,
	dietDayDateKey,
	dietDaySelectValue,
	findDietDayBySelectValue,
	isVirtualDietDay,
	resolveDietDaySelection,
} from "../utils/dietDays";
import {errorText, isTargetHasFoodError} from "../utils/foodDiary";

import {find} from "lodash";
import moment from "moment";

import {Input, Textarea} from "@mantine/core";

import {useCookies} from "react-cookie";

import ActionMenu from "./ActionMenu";
import ErrorModal from "./ErrorModal";
import FoodDiaryCard from "./FoodDiaryCard";
import Select from "./Select";
import { isRtlLanguage } from "../utils/rtl";

export default function FoodDiary(props) {
	const { t } = useTranslation();
    const { language } = useI18next();
	const isRtl = isRtlLanguage(language);
	const ms3 = isRtl ? "mr-3" : "ml-3";
	const ms2 = isRtl ? "mr-2" : "ml-2";
	const ms6 = isRtl ? "mr-6" : "ml-6";

	const [cookies] = useCookies(["token", "id"]);
	const [token] = useState(cookies["token"]);
	const [idUser] = useState(cookies["id"]);

	const [allowedFoods, setAllowedFoods] = useState([]);
	const [caloriesPer100g, setCaloriesPer100g] = useState("");

	const [selectValue, setSelectValue] = useState("");
	const [gramValue, setGramValue] = useState("");
	const [caloriesValue, setCaloriesValue] = useState("");
	const [timeValue, setTimeValue] = useState("");
	const [tableData, setTableData] = useState(null);
	const [comment, setComment] = useState("");
	const [showMessage, setShowMessage] = useState(false);
	const [saveCommentMessage, setSaveCommentMessage] = useState("");
	const [statusMessage, setStatusMessage] = useState("");
	const [countCalories, setCountCalories] = useState(false);

	const [openModal, setOpenModal] = useState(false);
	const [errors, setErrors] = useState([]);
	const [showCopySheet, setShowCopySheet] = useState(false);
	const [showCopyConflict, setShowCopyConflict] = useState(false);
	const [pendingCopy, setPendingCopy] = useState(null);

	const [selectedDayCaptionId, setSelectedDayCaptionId] = useState(null);
	const [dietDays, setDietDays] = useState([]);

	const dayCaptions = dietDays.map((item) => ({
		value: dietDaySelectValue(item),
		label: item.diaryDayCaption,
	}));

	const selectedDay = findDietDayBySelectValue(dietDays, selectedDayCaptionId);

	function refreshDietDays(preferredSelectValue) {
		return api
			.getDietDays(token, {id: idUser, virtualDays: true}, language)
			.then((res) => {
				const days = Array.isArray(res) ? res : [];
				setDietDays(days);
				const todayYmd = moment().format("YYYY-MM-DD");
				const {day} = resolveDietDaySelection(
					days,
					preferredSelectValue != null
						? preferredSelectValue
						: selectedDayCaptionId,
					todayYmd
				);
				if (day) {
					setSelectedDayCaptionId(dietDaySelectValue(day));
				}
				return days;
			});
	}

	function ensureDiaryId() {
		const selected = findDietDayBySelectValue(dietDays, selectedDayCaptionId);
		if (!selected) {
			return Promise.reject(new Error(t("food_diary_food_not_selected")));
		}
		if (!isVirtualDietDay(selected) && selected.id != null) {
			return Promise.resolve({
				diaryId: selected.id,
				selectValue: dietDaySelectValue(selected),
			});
		}
		if (!selected.date) {
			return Promise.reject(new Error(t("food_diary_food_not_selected")));
		}
		return api
			.ensureDiaryDay(token, {id: idUser, date: selected.date}, language)
			.then((ensured) => ({
				diaryId: ensured.id,
				selectValue: dietDaySelectValue(ensured),
			}));
	}

	useEffect(() => {
		refreshDietDays().catch((err) => console.log(err));
	}, [idUser, token, language]);

	useEffect(() => {
		api.getAllowedFoods(token, {id: idUser}, language).then((res) => {
			setAllowedFoods(Array.isArray(res) ? res : []);
		});
	}, [idUser, token, language]);

	const selectedDateKey = selectedDay
		? dietDayDateKey(selectedDay.date)
		: selectedDayCaptionId;

	useEffect(() => {
		setSaveCommentMessage("");
		setShowMessage(false);
		setTableData(null);
		setSelectValue("");
		setCaloriesPer100g("");
		setCaloriesValue("");
		setGramValue("");
		setComment("");
		setTimeValue("");
	}, [selectedDateKey, idUser, token]);

	useEffect(() => {
		if (!selectedDayCaptionId) {
			return;
		}

		const day = findDietDayBySelectValue(dietDays, selectedDayCaptionId);
		if (!day || isVirtualDietDay(day) || day.id == null) {
			setComment(day && day.notes ? day.notes : "");
			const shouldShowMessage = day && (day.phase === 2 || day.phase === 3);
			setShowMessage(!shouldShowMessage);
			return;
		}

		api
			.getFoodDiaryTable(token, {id: idUser, diaryId: day.id}, language)
			.then((res) => {
				setTableData(res.foodDiaryTableData);
			})
			.catch((err) => console.log(err));

		setComment(day.notes ?? "");
		const shouldShowMessage = day.phase === 2 || day.phase === 3;
		setShowMessage(!shouldShowMessage);
	}, [selectedDayCaptionId, dietDays, idUser, token]);

	function handleAddNoteClick(event) {
		event.preventDefault();
		ensureDiaryId()
			.then(({diaryId}) =>
				api.addFoodDiaryNote(
					token,
					{
						diaryId,
						note: comment,
						id: idUser,
					},
					language
				)
			)
			.then((res) => setSaveCommentMessage(t("food_diary_notes_saved")))
			.then(() => refreshDietDays(selectedDayCaptionId))
			.catch((err) => console.log(err));
	}

	useEffect(() => {
		if (!selectValue) {
			return;
		}
		const food = find(
			allowedFoods,
			(item) => String(item.id) === String(selectValue)
		);
		setCaloriesPer100g(food ? food.caloriesPer100g : "");
	}, [selectValue, allowedFoods]);

	useEffect(() => {
		if (countCalories)
			setCaloriesValue(
				parseFloat((gramValue / 100) * caloriesPer100g).toFixed(0)
			);
	}, [gramValue, caloriesPer100g, countCalories]);

	function checkSaveFoodDiaryInput() {
		if (selectValue !== "" && (gramValue === "" || caloriesValue === "")) {
			return {
				isValid: false,
				errors: [t("food_diary_calories_error")],
			};
		}
		if (selectValue === "" || gramValue === "" || caloriesValue === "") {
			return {isValid: false, errors: [t("food_diary_food_not_selected")]};
		}

		return {isValid: true};
	}

	function handleAddFoodDiary(event) {
		event.preventDefault();
		setErrors([]);

		const validationRes = checkSaveFoodDiaryInput();
		if (!validationRes.isValid) {
			setErrors([validationRes.errors]);
			setOpenModal(true);
			return;
		}

		let customTimeValue = " ";

		if (timeValue === "") {
			customTimeValue = " ";
		} else {
			customTimeValue = timeValue;
		}

		const food = find(
			allowedFoods,
			(item) => String(item.id) === String(selectValue)
		);
		const productName = allowedFoodLabel(food) || selectValue;

		ensureDiaryId()
			.then(({diaryId}) =>
				api
					.addFoodDiary(
						token,
						{
							id: idUser,
							diaryId,
							timeTaken: customTimeValue,
							productName,
							gram: parseFloat(gramValue),
							calories: parseFloat(caloriesValue),
						},
						language
					)
					.then(() => diaryId)
			)
			.then((diaryId) =>
				api.getFoodDiaryTable(
					token,
					{
						id: idUser,
						diaryId,
					},
					language
				)
			)
			.then((table) => {
				setTableData(table.foodDiaryTableData);
				return refreshDietDays(selectedDayCaptionId);
			})
			.then(() => {
				setCaloriesPer100g("");
				setCaloriesValue("");
				setSelectValue("");
				setGramValue("");
				setTimeValue("");
			})
			.catch((err) => console.log(err));
	}

	function handleCardClick(time, product, gram, calories) {
		setCountCalories(false);
		const match = find(
			allowedFoods,
			(item) => allowedFoodLabel(item) === product || item.name === product
		);
		setSelectValue(match ? String(match.id) : product);
		setTimeValue(time);
		setCaloriesValue(calories);
		setGramValue(gram);
	}

	function showCopyError(err) {
		const message = errorText(err);
		setErrors([message || t("error_label")]);
		setOpenModal(true);
	}

	function runCopy(fromDiaryId, toDate, mode) {
		setStatusMessage("");
		const body = {
			id: idUser,
			fromDiaryId,
			toDate,
		};
		if (mode) {
			body.mode = mode;
		}
		return api
			.copyFoodDiary(token, body, language)
			.then(() => {
				setPendingCopy(null);
				setShowCopyConflict(false);
				setStatusMessage(t("food_copied"));
				return refreshDietDays(`date:${toDate}`);
			})
			.catch((err) =>
				refreshDietDays(`date:${toDate}`).then(() => {
					if (isTargetHasFoodError(err)) {
						setPendingCopy({fromDiaryId, toDate});
						setShowCopyConflict(true);
						return;
					}
					showCopyError(err);
				})
			);
	}

	function copyToDate(toDate) {
		ensureDiaryId()
			.then(({diaryId}) => runCopy(diaryId, toDate))
			.catch((err) => showCopyError(err));
	}

	function setMealTemplate() {
		setStatusMessage("");
		ensureDiaryId()
			.then(({diaryId}) =>
				api.setFoodTemplate(token, {id: idUser, diaryId}, language)
			)
			.then(() => setStatusMessage(t("food_template_saved")))
			.catch((err) => showCopyError(err));
	}

	function clearMealTemplate() {
		setStatusMessage("");
		api
			.setFoodTemplate(token, {id: idUser, diaryId: null}, language)
			.then(() => setStatusMessage(t("food_template_cleared")))
			.catch((err) => showCopyError(err));
	}

	const hasMeals = Array.isArray(tableData) && tableData.length > 0;
	const copySheetActions = [
		...(hasMeals
			? [
					{
						label: t("food_copy_today"),
						testId: "food-copy-today",
						onPress: () => copyToDate(moment().format("YYYY-MM-DD")),
					},
					{
						label: t("food_copy_tomorrow"),
						testId: "food-copy-tomorrow",
						onPress: () =>
							copyToDate(moment().add(1, "day").format("YYYY-MM-DD")),
					},
					{
						label: t("food_use_as_meal_template"),
						testId: "food-use-template",
						onPress: setMealTemplate,
					},
			  ]
			: []),
		{
			label: t("food_clear_meal_template"),
			testId: "food-clear-template",
			onPress: clearMealTemplate,
		},
	];
	const conflictActions = pendingCopy
		? [
				{
					label: t("food_diary_overwrite"),
					testId: "food-copy-overwrite",
					onPress: () =>
						runCopy(pendingCopy.fromDiaryId, pendingCopy.toDate, "overwrite"),
				},
				{
					label: t("food_diary_append"),
					testId: "food-copy-append",
					onPress: () =>
						runCopy(pendingCopy.fromDiaryId, pendingCopy.toDate, "append"),
				},
		  ]
		: [];

	return (
		<>
			<div className={"h-full md:h-650 flex flex-col md:flex-row"}>
				<form
					className={"md:w-6/12 lg:w-3/12 flex flex-col mt-4"}
					onSubmit={handleAddFoodDiary}
				>
					<p className={`text-xs ${ms3}`}>{t("food_diary_day")}</p>
					<div className={"h-11 mt-2"}>
						<Select
							data={dayCaptions}
							value={selectedDayCaptionId}
							setValue={setSelectedDayCaptionId}
							selectedDietDay={true}
						/>
					</div>

					<div className={"flex mt-3"}>
						<div className={"w-full"}>
							<p className={`text-xs ${ms3}`}>{t("food_diary_allowed_food")}</p>
							<div className={"mt-2"}>
								<Select
									data={allowedFoods.map((item) => {
										return {value: String(item.id), label: allowedFoodLabel(item)};
									})}
									selectedDietDay={selectedDayCaptionId}
									value={selectValue}
									setValue={setSelectValue}
									searchable
									textLeft
									styles={{
										dropdown: {overflow: "hidden"},
										root: {height: "45px"},
									}}
								/>
							</div>
						</div>

						{/*Mobile version*/}
					<div className={`block md:hidden w-6/12 h-full ${ms3} mt-0`}>
						<p className={`text-xs ${ms3}`}>{t("food_diary_time")}</p>
							<Input
								className={"mt-2 h-10"}
								radius={"xl"}
								disabled={!selectedDayCaptionId}
								value={timeValue}
								onChange={(event) => setTimeValue(event.target.value)}
								styles={{
									input: {height: "100%"},
									wrapper: {height: "100%", marginTop: "9px"},
									root: {height: "45px"},
								}}
							/>
						</div>
					</div>

					<div className={"flex w-full justify-between mt-4 md:mt-3"}>
						<div className={"max-h-full"}>
							<p className={`text-xs ${ms3}`}>{t("food_diary_grams")}</p>
							<Input
								radius={"xl"}
								placeholder={t("food_diary_grams_placeholder")}
								disabled={!selectValue}
								value={gramValue}
								onChange={(event) => {
									setCountCalories(true);
									setGramValue(event.currentTarget.value);
								}}
								className={"h-11"}
								styles={{
									wrapper: {marginTop: "9px", height: "100%"},
									input: {height: "100%"},
								}}
							/>
						</div>
					<div className={`max-h-full mt-0 ${ms2}`}>
						<p className={`text-xs ${ms3}`}>{t("food_diary_calories")}</p>
							<Input
								radius={"xl"}
								placeholder={t("food_diary_calories_placeholder")}
								disabled={!selectValue}
								value={caloriesValue}
								onChange={(event) => setCaloriesValue(event.target.value)}
								className={"h-11"}
								styles={{
									wrapper: {marginTop: "9px", height: "100%"},
									input: {height: "100%"},
								}}
							/>
						</div>
					</div>
					<p className={`hidden md:block text-xs ${ms3} mt-3`}>{t("food_diary_time")}</p>
					<Input
						className={"hidden md:block w-full h-11 mt-2"}
						radius={"xl"}
						disabled={!selectedDayCaptionId}
						value={timeValue}
						onChange={(event) => setTimeValue(event.target.value)}
						styles={{input: {height: "100%"}}}
					/>
					<div className={"flex w-full gap-3 mt-4"}>
						<button
							type={"submit"}
							disabled={!selectedDayCaptionId}
							className={
								"flex-1 min-h-11 py-2 px-2 disabled:bg-gray bg-green rounded-3xl text-white text-sm whitespace-nowrap"
							}
						>
							{t("food_diary_submit")}
						</button>
						<button
							type={"button"}
							disabled={!selectedDayCaptionId}
							onClick={() => setShowCopySheet(true)}
							className={
								"flex-1 min-h-11 py-2 px-2 disabled:bg-gray bg-green rounded-3xl text-white text-sm whitespace-nowrap"
							}
						>
							{t("food_diary_copy")}
						</button>
					</div>
					{statusMessage ? (
						<p className={`mt-3 text-sm text-green ${ms3}`}>{statusMessage}</p>
					) : null}
					{showMessage ? (
						<p className={"text-center mt-4 text-sm"}>
							{t("food_diary_message")}
						</p>
					) : (
						""
					)}
				</form>
				<div
				className={`flex flex-col w-full md:6/12 lg:w-9/12 ${isRtl ? "mr-0 md:mr-6" : "ml-0 md:ml-6"} mt-5 md:mt-0 h-full`}
				>
					<div
						className={"w-full rounded-xl shadow py-2 overflow-y-auto h-460"}
					>
						<div
							className={
								"w-full grid grid-cols-8 px-2 md:px-6 gap-x-3 text-xs text-gray"
							}
						>
							<div className={"grid grid-cols-6 col-span-7"}>
								<h4 className={"justify-self-center"}>{t("food_diary_time")}</h4>
								<h4 className={"justify-self-center col-start-2 col-span-2"}>
									{t("food_diary_food")}
								</h4>
								<h4 className={"justify-self-center"}>{t("food_diary_grams")}</h4>
								<h4 className={"justify-self-center col-start-5"}>{t("food_diary_calories")}</h4>
								<h4 className={"justify-self-center"}>{t("food_diary_sum")}</h4>
							</div>
							<div></div>
						</div>
						{tableData?.map((item) => (
							<FoodDiaryCard
								key={item.id}
								diaryId={selectedDay && selectedDay.id}
								dailyFoodDiaryId={item.id}
								setTableData={setTableData}
								handleCardClick={handleCardClick}
								time={item.timeTaken}
								product={item.productName}
								gram={item.gram}
								cal={item.calories}
								amount={item.caloriesSum}
							/>
						))}
					</div>
					{saveCommentMessage ? (
						<p className={`mt-3 text-sm text-green ${ms3}`}>
							{saveCommentMessage}
						</p>
					) : (
						""
					)}
					<form
						onSubmit={handleAddNoteClick}
						className={"hidden md:flex flex-col items-center"}
					>
						<Textarea
							placeholder={t("food_diary_notes_placeholder")}
							radius="lg"
							value={comment}
							onChange={(event) => {
								setComment(event.currentTarget.value);
								setSaveCommentMessage("");
							}}
							className={"hidden md:block h-24 w-full"}
							styles={{
								wrapper: {height: "100%"},
								input: {height: "100%"},
							}}
						/>
						<button
							type={"submit"}
							className={"hidden md:block bg-green rounded-3xl text-white text-sm mt-5 py-3 w-32"}
						>
							{t("food_diary_save")}
						</button>
					</form>
				</div>

				{/*Mobile version*/}
				<form onSubmit={handleAddNoteClick} className={"flex flex-col items-center md:hidden"}>
					<Textarea
						placeholder={t("food_diary_notes_placeholder")}
						radius="lg"
						value={comment}
						onChange={(event) => {
							setComment(event.currentTarget.value);
							setSaveCommentMessage("");
						}}
						className={"block w-full h-28"}
						styles={{wrapper: {height: "100%"}, input: {height: "100%"}}}
					/>
					<button
						type={"submit"}
						className={
							"w-32 bg-green rounded-3xl text-white text-sm mt-5 py-2"
						}
					>
						{t("food_diary_save")}
					</button>
				</form>
			</div>
				<ErrorModal
					title={" "}
					opened={openModal}
					setOpened={setOpenModal}
					errors={errors}
				/>
				<ActionMenu
					opened={showCopySheet}
					title={t("food_diary_copy")}
					message={t("food_copy_choose_target")}
					cancelLabel={t("cancel")}
					testId="food-copy-sheet"
					cancelTestId="food-copy-sheet-cancel"
					actions={copySheetActions}
					onClose={() => setShowCopySheet(false)}
				/>
				<ActionMenu
					opened={showCopyConflict}
					title={t("warning_title")}
					message={t("food_copy_target_has_meals")}
					cancelLabel={t("cancel")}
					testId="food-copy-conflict"
					cancelTestId="food-copy-conflict-cancel"
					actions={conflictActions}
					onClose={() => {
						setShowCopyConflict(false);
						setPendingCopy(null);
					}}
				/>
		</>
	);
}
